import {
	ButtonDropdownField,
	Checkbox,
	DropdownList,
	FlexColumn,
	FlexRow,
	Label,
	Text,
	TextField,
	IconTooltip,
} from '@forecast-it/design-system';
import React, {useEffect, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {FEATURE_FLAGS} from '../../../../constants';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {CustomFieldType, CustomFieldTypeLabels, EntityType, EntityTypeLabels, FF_GATED_CUSTOM_FIELD_TYPES} from '../Constants';
import {AvaRefOption} from './ava-custom-field-dropdown/useAvaRefOptions';
import styled from 'styled-components';

type CustomFieldDefinitionFormProps = {
	entityType: EntityType;
	setEntityType?: (value: EntityType) => void;
	displayName: string;
	setDisplayName: (value: string) => void;
	fieldKey: string;
	setFieldKey?: (value: string) => void;
	customFieldType: CustomFieldType;
	setCustomFieldType?: (value: CustomFieldType) => void;
	avaRefOptions: AvaRefOption[] | null;
	selectedAvaRef: AvaRefOption;
	selectAvaRefOption: (refOption: AvaRefOption) => void;
	readOnly: boolean;
	setReadOnly: (value: boolean) => void;
	errorMessage?: string | undefined;
};

const StyledContainer = styled.div`
	width: 600px;
`;

export const CustomFieldDefinitionForm = ({
	entityType,
	setEntityType,
	displayName,
	setDisplayName,
	fieldKey,
	setFieldKey,
	customFieldType,
	setCustomFieldType,
	avaRefOptions,
	selectedAvaRef,
	selectAvaRefOption,
	readOnly,
	setReadOnly,
	errorMessage,
}: CustomFieldDefinitionFormProps): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const dummyRef = useRef<HTMLDivElement>(null);
	const hasIntegerDecimalDateFF = hasFeatureFlag(FEATURE_FLAGS.INTEGER_DECIMAL_DATE_CUSTOM_FIELDS);

	const visibleCustomFieldTypeLabels = Object.entries(CustomFieldTypeLabels).filter(
		([key]) => !FF_GATED_CUSTOM_FIELD_TYPES.has(key as CustomFieldType) || hasIntegerDecimalDateFF
	) as [CustomFieldType, string][];

	useEffect(() => {
		//Fix buggy Radix button focus on open modal
		const timeout = setTimeout(() => {
			dummyRef.current?.focus();
		}, 0);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<StyledContainer>
			<FlexColumn gap={'l'}>
				<FlexColumn>
					<FlexRow>
						<div ref={dummyRef} tabIndex={-1} />
						<Label>
							<FormattedMessage id={'settings.custom-fields.applies_to'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'settings.custom-fields.applies_to'})}
								details={formatMessage({
									id: 'settings.custom-fields.create_modal.tooltip.applies_to.description',
								})}
							/>
						</IconTooltip>
					</FlexRow>
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdownField disabled={!setEntityType}>
								<Text ellipsis={true} size={'1'}>
									{EntityTypeLabels[entityType]}
								</Text>
							</ButtonDropdownField>
						</DropdownList.Trigger>
						{setEntityType && (
							<DropdownList.Content offset={10} useTriggerWidth>
								{Object.entries(EntityTypeLabels).map(([key, label]: [EntityType, string]) => (
									<DropdownList.Item key={key} onClick={() => setEntityType(key)}>
										{label}
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						)}
					</DropdownList>
				</FlexColumn>
				<FlexColumn>
					<FlexRow>
						<Label size={'2'}>
							<FormattedMessage id={'settings.custom-fields.displayName'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'settings.custom-fields.displayName'})}
								details={formatMessage({
									id: 'settings.custom-fields.create_modal.tooltip.display_name.description',
								})}
							/>
						</IconTooltip>
					</FlexRow>
					<TextField
						value={displayName}
						onValueChange={setDisplayName}
						size="large"
						width="flex"
						data-cy={'custom-field-display-name'}
					/>
				</FlexColumn>
				<FlexColumn>
					<FlexRow>
						<Label>
							<FormattedMessage id={'settings.custom-fields.key'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'settings.custom-fields.key'})}
								details={formatMessage({id: 'settings.custom-fields.create_modal.tooltip.key.description'})}
							/>
						</IconTooltip>
					</FlexRow>
					<TextField
						disabled={!setFieldKey}
						value={fieldKey}
						onValueChange={setFieldKey}
						size="large"
						width="flex"
						data-cy="custom-field-key"
					/>
					{!!errorMessage && <Text color="error">{errorMessage}</Text>}
				</FlexColumn>
				<FlexColumn>
					<FlexRow>
						<Label>
							<FormattedMessage id={'common.type'} />
						</Label>
						<IconTooltip>
							<IconTooltip.Item
								title={formatMessage({id: 'common.type'})}
								details={formatMessage({id: 'settings.custom-fields.create_modal.tooltip.type.description'})}
							/>
						</IconTooltip>
					</FlexRow>
					<DropdownList>
						<DropdownList.Trigger>
							<ButtonDropdownField disabled={!setCustomFieldType}>
								<Text ellipsis={true} size={'1'}>
									{CustomFieldTypeLabels[customFieldType]}
								</Text>
							</ButtonDropdownField>
						</DropdownList.Trigger>
						{setCustomFieldType && (
							<DropdownList.Content offset={10} useTriggerWidth>
								{visibleCustomFieldTypeLabels.map(([key, label]) => (
									<DropdownList.Item key={key} onClick={() => setCustomFieldType(key)}>
										{label}
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						)}
					</DropdownList>
				</FlexColumn>
				<FlexColumn gap={'l'}>
					{!!avaRefOptions && (
						<FlexColumn>
							<FlexRow>
								<Label>
									<FormattedMessage id={'settings.custom-fields.advanced_analytics_field_label'} />
								</Label>
								<IconTooltip>
									<IconTooltip.Item
										title={formatMessage({
											id: 'settings.custom-fields.create_modal.tooltip.advanced_analytics_field.title',
										})}
										details={formatMessage({
											id: 'settings.custom-fields.create_modal.tooltip.advanced_analytics_field.description',
										})}
									/>
								</IconTooltip>
							</FlexRow>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdownField>
										<Text ellipsis={true} size={'1'}>
											{selectedAvaRef.label}
										</Text>
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content offset={10} useTriggerWidth>
									{avaRefOptions.map(option => (
										<DropdownList.Item
											key={option.value}
											disabled={option.disabled}
											onClick={() => selectAvaRefOption(option)}
										>
											{option.label}
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</FlexColumn>
					)}

					{(customFieldType === CustomFieldType.STRING ||
						customFieldType === CustomFieldType.INTEGER ||
						customFieldType === CustomFieldType.DECIMAL ||
						customFieldType === CustomFieldType.DATE) && (
						<Checkbox
							checked={readOnly}
							onChange={() => setReadOnly(!readOnly)}
							labelText={formatMessage({id: 'settings.custom-fields.readOnly'})}
							notBoldLabel={true}
						/>
					)}
				</FlexColumn>
			</FlexColumn>
		</StyledContainer>
	);
};
